// Helpers for the `transaction.billingAccountBalance = { before, after }` audit field.
//
// The field is written by the API on every transaction that touches a billing
// account, so a disputed movement can be read off a single transaction. It is
// absent on transactions predating the field (and on any that the production
// backfill could not attribute to a billing account), so every helper here has
// to cope with it missing.

export const hasBalance = (transaction) =>
  Number.isFinite(transaction?.billingAccountBalance?.before) &&
  Number.isFinite(transaction?.billingAccountBalance?.after)

export const formatBalanceAmount = (amount, currency) => {
  if (!Number.isFinite(amount)) return '-'
  const decimalPlaces = currency?.decimalPlaces ?? 2
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })
  const sign = amount < 0 ? '-' : ''
  return currency?.code
    ? `${sign}${currency.code} ${formatted}`
    : `${sign}${formatted}`
}

// The signed movement this transaction applied to the balance. Taken from the
// recorded before/after rather than from `amount`, so a transaction that moved
// no balance at all — a postpaid charge, settled later on an invoice — reads as
// 0 instead of as its own cost.
export const balanceDelta = (transaction) =>
  hasBalance(transaction)
    ? transaction.billingAccountBalance.after -
      transaction.billingAccountBalance.before
    : null

// Build the audit trail for a page of transactions.
//
// `transactions` arrives newest-first (the API sorts by createdAt descending)
// and the timeline reads the same way, so the balance the customer holds today
// sits at the top and each step downwards undoes one movement.
//
// Every entry is checked two ways:
//   - `selfConsistent`: does before + amount land on after? A mismatch means the
//     recorded movement disagrees with the recorded amount.
//   - `continuous`: does this entry's `after` match the `before` of the entry
//     above it (the next one chronologically)? A break means the balance moved
//     between the two without a transaction to account for it.
// A tolerance of half a minor unit absorbs the currency rounding rather than
// flagging it.
export const buildBalanceTimeline = (transactions = []) => {
  const entries = transactions.map((transaction) => {
    const currency = transaction.country?.currency
    const tolerance = 0.5 / Math.pow(10, currency?.decimalPlaces ?? 2)
    const recorded = hasBalance(transaction)
    const delta = balanceDelta(transaction)
    const signedAmount = transaction.isCredit
      ? Number(transaction.amount)
      : -Number(transaction.amount)
    // A movement of 0 against a non-zero amount is not a discrepancy: it is a
    // postpaid charge, which is invoiced instead of deducted.
    const settledOnInvoice = recorded && delta === 0 && signedAmount !== 0

    return {
      transaction,
      currency,
      tolerance,
      recorded,
      delta,
      settledOnInvoice,
      selfConsistent:
        !recorded ||
        settledOnInvoice ||
        Math.abs(
          transaction.billingAccountBalance.before +
            signedAmount -
            transaction.billingAccountBalance.after,
        ) <= tolerance,
      continuous: true,
      gap: null,
    }
  })

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    if (!entry.recorded) continue

    // The nearest NEWER transaction on the same balance chain — not simply the
    // previous row. A user can hold a billing account in several countries, and
    // the API returns one list sorted by date across all of them, so adjacent
    // rows are routinely different wallets in different currencies. Comparing
    // across that boundary invents a gap out of two unrelated balances.
    //
    // If the nearest same-chain neighbour has no recorded balance, there is no
    // comparison to make: whatever it moved is unknown, so a difference here
    // proves nothing.
    let newer = null
    for (let j = i - 1; j >= 0; j--) {
      if (!isSameBalanceChain(entries[j].transaction, entry.transaction))
        continue
      newer = entries[j].recorded ? entries[j] : null
      break
    }
    if (!newer) continue

    const gap =
      newer.transaction.billingAccountBalance.before -
      entry.transaction.billingAccountBalance.after
    if (Math.abs(gap) > entry.tolerance) {
      entry.continuous = false
      entry.gap = gap
    }
  }

  return entries
}

// Two transactions sit on the same running balance when they belong to the same
// country's wallet. `billingAccountId` narrows it further for a corporate
// holding several accounts in one country — but only when both sides carry one,
// since transactions written before it was populated have none and would
// otherwise look like a separate chain.
const isSameBalanceChain = (a, b) => {
  if (idOf(a.country?._id) !== idOf(b.country?._id)) return false
  if (!a.billingAccountId || !b.billingAccountId) return true
  return idOf(a.billingAccountId) === idOf(b.billingAccountId)
}

const idOf = (value) => (value == null ? '' : String(value))
